import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { MdPlayArrow, MdFavorite, MdFavoriteBorder, MdSettings } from 'react-icons/md';
import { StreamPlayerProvider, useStreamPlayer } from './context/StreamPlayerContext';
import { ModeTabs } from './components/ModeTabs';
import { SearchBar } from './components/SearchBar';
import { BrowseCategoryColumn } from './components/BrowseCategoryColumn';
import { ResizableBrowseSplit } from './components/ResizableBrowseSplit';
import { ResizableBrowsePlayerSplit } from './components/ResizableBrowsePlayerSplit';
import { CategoryRail } from './components/CategoryRail';
import { ChannelList } from './components/ChannelList';
import { PosterGrid } from './components/PosterGrid';
import { PlayerShell } from './components/PlayerShell';
import { EmptyState } from './components/EmptyState';
import { catalogLoadErrorTitle } from './components/ErrorState';
import { ServerCard } from './components/ServerCard';
import { SeriesBrowser } from './components/SeriesBrowser';
import { ServerSwitcher } from './components/ServerSwitcher';
import { AddServerModal } from './components/AddServerModal';
import { EditServerModal } from './components/EditServerModal';
import { RightDetailPanel } from './components/RightDetailPanel';
import type { EpisodeItem, LiveChannel, RecentEntry, SeriesItem, StreamServer, VodItem } from './types';
import { xtreamLogin } from './adapters/xtreamAdapter';
import { M3U_DEFAULT_FETCH_TIMEOUT_MS } from './adapters/m3uParser';
import {
  clearAllStreamPlayerData,
  exportLocalConfig,
  importLocalConfig,
} from './storage/streamPlayerStorage';
import {
  streamGhostOnDarkHover,
  streamOutlineOnDarkHover,
  streamRedOutlineOnDarkHover,
  streamTealOutlineHover,
} from './darkThemeButtonProps';

const SHELL_HEIGHT = 'calc(100dvh - 96px)';

/** Substring filter only when the query has at least 2 non-whitespace chars (instant, no debounce). */
function applyNameFilterMin2<T extends { name: string }>(list: T[], q: string): T[] {
  const s = q.trim().toLowerCase();
  if (s.length < 2) return list;
  return list.filter((x) => x.name.toLowerCase().includes(s));
}

function normCatId(id: string): string {
  return String(id).trim();
}

const SERIES_PAGE = 150;

function Inner() {
  const toast = useToast();
  const isLg = useBreakpointValue({ base: false, lg: true });
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<StreamServer | null>(null);
  const liveListScrollRef = useRef<HTMLDivElement>(null);
  const vodListScrollRef = useRef<HTMLDivElement>(null);
  const [seriesVisible, setSeriesVisible] = useState(SERIES_PAGE);
  const [liveCatSearch, setLiveCatSearch] = useState('');
  const [liveChSearch, setLiveChSearch] = useState('');
  const [vodCatSearch, setVodCatSearch] = useState('');
  const [vodItemSearch, setVodItemSearch] = useState('');
  const [seriesCatSearch, setSeriesCatSearch] = useState('');
  const [seriesItemSearch, setSeriesItemSearch] = useState('');

  const sp = useStreamPlayer();
  const {
    nav,
    browseTab,
    activeSourceId,
    catalog,
    loading,
    loadLabel,
    error,
    searchQuery,
    selectedLiveCategory,
    selectedVodCategory,
    selectedSeriesCategory,
    playback,
    miniPlayer,
    favorites,
    recents,
    continueWatching,
    settings,
    lastPlayed,
    detailVod,
    detailSeries,
    servers,
    activeServer,
    cancelCatalogLoad,
    loadServerCatalog,
    clearError,
    saveStreamServer,
    updateStreamServer,
    removeServer,
    duplicateServer,
    openNav,
    setBrowseTab,
    setSearchQuery,
    setLiveCategory,
    setVodCategory,
    setSeriesCategory,
    playItem,
    stopPlayback,
    setMiniPlayer,
    toggleFavoriteFromLive,
    toggleFavoriteFromVod,
    toggleFavoriteFromSeries,
    toggleFavoriteFromEpisode,
    isFavorite,
    updateSettings,
    recordProgress,
    closeVodDetail,
    openSeriesDetail,
    closeSeriesDetail,
    ensureSeriesLoaded,
  } = sp;

  const playbackRef = useRef(playback);
  playbackRef.current = playback;

  const onPlayerProgress = useCallback(
    (pos: number, dur: number) => {
      const p = playbackRef.current;
      if (p) recordProgress(p, pos, dur);
    },
    [recordProgress],
  );

  const embeddedPanelVideoMaxH = useMemo(
    () => (browseTab === 'live' && playback?.isLive ? 'min(52vh, 480px)' : 'min(36vh, 300px)'),
    [browseTab, playback?.isLive],
  );

  const onExpandMiniPlayer = useCallback(() => setMiniPlayer(true), [setMiniPlayer]);
  const onCollapseMiniPlayer = useCallback(() => setMiniPlayer(false), [setMiniPlayer]);
  const onMiniToggleNoop = useCallback(() => {}, []);

  useEffect(() => {
    setLiveCatSearch('');
    setLiveChSearch('');
    setVodCatSearch('');
    setVodItemSearch('');
    setSeriesCatSearch('');
    setSeriesItemSearch('');
  }, [browseTab]);

  useEffect(() => {
    setSeriesVisible(SERIES_PAGE);
  }, [browseTab, selectedSeriesCategory, seriesItemSearch]);

  useEffect(() => {
    liveListScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [selectedLiveCategory]);

  useEffect(() => {
    vodListScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [selectedVodCategory]);

  const seriesDetail = detailSeries && catalog ? catalog.seriesDetails[detailSeries.id] ?? null : null;

  const displayLiveCategories = useMemo(() => {
    if (!catalog) return [];
    return applyNameFilterMin2(catalog.liveCategories, liveCatSearch);
  }, [catalog, liveCatSearch]);

  const displayVodCategories = useMemo(() => {
    if (!catalog) return [];
    return applyNameFilterMin2(catalog.vodCategories, vodCatSearch);
  }, [catalog, vodCatSearch]);

  const displaySeriesCategories = useMemo(() => {
    if (!catalog) return [];
    return applyNameFilterMin2(catalog.seriesCategories, seriesCatSearch);
  }, [catalog, seriesCatSearch]);

  useEffect(() => {
    if (!catalog || browseTab !== 'live') return;
    const ids = new Set(displayLiveCategories.map((c) => normCatId(c.id)));
    if (selectedLiveCategory !== 'all' && !ids.has(normCatId(selectedLiveCategory))) {
      setLiveCategory('all');
    }
  }, [catalog, browseTab, displayLiveCategories, selectedLiveCategory, setLiveCategory]);

  useEffect(() => {
    if (!catalog || browseTab !== 'movies') return;
    const ids = new Set(displayVodCategories.map((c) => normCatId(c.id)));
    if (selectedVodCategory !== 'all' && !ids.has(normCatId(selectedVodCategory))) {
      setVodCategory('all');
    }
  }, [catalog, browseTab, displayVodCategories, selectedVodCategory, setVodCategory]);

  useEffect(() => {
    if (!catalog || browseTab !== 'series') return;
    const ids = new Set(displaySeriesCategories.map((c) => normCatId(c.id)));
    if (selectedSeriesCategory !== 'all' && !ids.has(normCatId(selectedSeriesCategory))) {
      setSeriesCategory('all');
    }
  }, [catalog, browseTab, displaySeriesCategories, selectedSeriesCategory, setSeriesCategory]);

  const switcherLabel = activeServer?.name ?? 'Server';
  const activeServerIdSetting = settings.activeServerId;

  const filteredLive = useMemo(() => {
    if (!catalog) return [];
    let list = catalog.liveChannels;
    if (selectedLiveCategory !== 'all') {
      const sel = normCatId(selectedLiveCategory);
      list = list.filter((c) => normCatId(c.categoryId) === sel);
    }
    return applyNameFilterMin2(list, liveChSearch);
  }, [catalog, selectedLiveCategory, liveChSearch]);

  const filteredVod = useMemo(() => {
    if (!catalog) return [];
    let list = catalog.vodItems;
    if (selectedVodCategory !== 'all') {
      const sel = normCatId(selectedVodCategory);
      list = list.filter((c) => normCatId(c.categoryId) === sel);
    }
    return applyNameFilterMin2(list, vodItemSearch);
  }, [catalog, selectedVodCategory, vodItemSearch]);

  const filteredSeries = useMemo(() => {
    if (!catalog) return [];
    let list = catalog.seriesItems;
    if (selectedSeriesCategory !== 'all') {
      const sel = normCatId(selectedSeriesCategory);
      list = list.filter((c) => normCatId(c.categoryId) === sel);
    }
    return applyNameFilterMin2(list, seriesItemSearch);
  }, [catalog, selectedSeriesCategory, seriesItemSearch]);

  const favFiltered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) return favorites;
    return favorites.filter((f) => f.title.toLowerCase().includes(q));
  }, [favorites, searchQuery]);

  const playLive = useCallback(
    (ch: LiveChannel) => {
      const url = ch.streamUrl;
      if (!url) {
        toast({ title: 'No stream URL', status: 'warning', duration: 3000 });
        return;
      }
      playItem({
        id: ch.id,
        kind: 'live',
        title: ch.name,
        streamUrl: url,
        poster: ch.logo,
        isLive: true,
      });
    },
    [playItem, toast],
  );

  const playVod = useCallback(
    (v: VodItem) => {
      const url = v.streamUrl;
      if (!url) {
        toast({ title: 'No stream URL', status: 'warning', duration: 3000 });
        return;
      }
      playItem({
        id: v.id,
        kind: 'vod',
        title: v.name,
        streamUrl: url,
        poster: v.poster,
        isLive: false,
      });
      closeVodDetail();
    },
    [playItem, closeVodDetail, toast],
  );

  const playEpisode = useCallback(
    (ep: EpisodeItem, series: SeriesItem) => {
      const url = ep.streamUrl;
      if (!url) {
        toast({ title: 'No stream URL', status: 'warning', duration: 3000 });
        return;
      }
      playItem({
        id: ep.id,
        kind: 'episode',
        title: `${series.name} · ${ep.title}`,
        streamUrl: url,
        poster: series.poster,
        isLive: false,
        seriesId: series.id,
        seasonNumber: ep.seasonNumber,
        episodeNumber: ep.episodeNumber,
      });
      closeSeriesDetail();
    },
    [playItem, closeSeriesDetail, toast],
  );

  const resumeFromRecent = useCallback(
    (r: RecentEntry) => {
      playItem({
        id: r.refId,
        kind: r.kind,
        title: r.title,
        streamUrl: r.streamUrl,
        poster: r.poster,
        isLive: r.kind === 'live',
        durationSeconds: r.durationSeconds,
      });
    },
    [playItem],
  );

  const resolveFavoritePlay = useCallback(
    (f: (typeof favorites)[0]) => {
      if (f.streamUrl) {
        playItem({
          id: f.refId,
          kind: f.kind === 'episode' ? 'episode' : f.kind === 'vod' ? 'vod' : 'live',
          title: f.title,
          streamUrl: f.streamUrl,
          poster: f.poster,
          isLive: f.kind === 'live',
          seriesId: f.seriesId,
        });
        return;
      }
      if (!catalog || f.sourceId !== activeSourceId) {
        toast({
          title: 'Switch source',
          description: 'Load the same library this favorite came from.',
          status: 'info',
          duration: 4000,
        });
        return;
      }
      if (f.kind === 'live') {
        const ch = catalog.liveChannels.find((c) => c.id === f.refId);
        if (ch) playLive(ch);
      } else if (f.kind === 'vod') {
        const v = catalog.vodItems.find((x) => x.id === f.refId);
        if (v) playVod(v);
      } else if (f.kind === 'series') {
        const s = catalog.seriesItems.find((x) => x.id === f.refId);
        if (s) openSeriesDetail(s);
      } else if (f.kind === 'episode') {
        const s = catalog.seriesItems.find((x) => x.id === f.seriesId);
        if (s) openSeriesDetail(s);
      }
    },
    [catalog, activeSourceId, playItem, playLive, playVod, openSeriesDetail, toast],
  );

  const handleTestXtream = useCallback(
    async (s: StreamServer) => {
      const kind = s.sourceKind ?? 'xtream';
      if (kind !== 'xtream') return;
      try {
        await xtreamLogin(s.server, s.username, s.password);
        toast({ title: 'Connection OK', status: 'success', duration: 2000 });
      } catch (e) {
        toast({
          title: 'Test failed',
          description: e instanceof Error ? e.message : '',
          status: 'error',
          duration: 5000,
        });
      }
    },
    [toast],
  );

  const showPlayerInline = Boolean(playback && !miniPlayer);
  const showRightColumn =
    isLg &&
    nav === 'browse' &&
    (browseTab === 'live' || showPlayerInline || detailVod || detailSeries);

  const rightPanelTitle =
    playback && !miniPlayer
      ? 'Player'
      : detailVod
        ? 'Movie'
        : detailSeries
          ? 'Series'
          : browseTab === 'live'
            ? 'Player'
            : undefined;

  const renderBrowseCenter = () => {
    if (!catalog) {
      if (loading) {
        const m3uLoad = activeServer?.sourceKind === 'm3u_url';
        return (
          <EmptyState
            title="Loading library"
            description={
              loadLabel ??
              (m3uLoad
                ? `Playlist URL loads are limited to about ${Math.round(
                    M3U_DEFAULT_FETCH_TIMEOUT_MS / 1000,
                  )} seconds; you can cancel anytime. Very large files may need **Upload M3U** instead.`
                : 'Downloading or parsing can take a while for large libraries.')
            }
          >
            <HStack spacing={4} justify="center">
              <Spinner size="lg" color="teal.300" thickness="3px" />
              {m3uLoad && (
                <Button size="sm" variant="outline" borderColor="whiteAlpha.400" onClick={cancelCatalogLoad}>
                  Cancel
                </Button>
              )}
            </HStack>
          </EmptyState>
        );
      }
      if (error) {
        return (
          <EmptyState
            title="Unable to load library"
            description="Details are shown in the dialog. You can dismiss it or retry from there."
          />
        );
      }
      return (
        <EmptyState title="No library" description="Add an IPTV server from the server menu, then connect to load your library." />
      );
    }

    if (browseTab === 'favorites') {
      return favFiltered.length === 0 ? (
        <EmptyState icon={MdFavorite} title="No favorites" description="Save channels and titles while browsing." />
      ) : (
        <VStack align="stretch" spacing={1}>
          {favFiltered.map((f) => (
            <Flex
              key={f.key}
              p={2}
              borderRadius="md"
              bg="whiteAlpha.50"
              borderWidth="1px"
              borderColor="whiteAlpha.100"
              justify="space-between"
              align="center"
            >
              <Box minW={0}>
                <HStack>
                  <Badge colorScheme="pink" variant="subtle" fontSize="10px">
                    {f.kind}
                  </Badge>
                  <Text fontWeight="600" color="white" fontSize="sm" noOfLines={1}>
                    {f.title}
                  </Text>
                </HStack>
              </Box>
              <Button size="xs" colorScheme="teal" onClick={() => resolveFavoritePlay(f)}>
                Open
              </Button>
            </Flex>
          ))}
        </VStack>
      );
    }

    if (browseTab === 'live') {
      if (isLg) {
        return (
          <ResizableBrowseSplit
            categoryColumn={
              <BrowseCategoryColumn
                categories={displayLiveCategories}
                selectedId={selectedLiveCategory}
                onSelect={setLiveCategory}
                categorySearch={liveCatSearch}
                onCategorySearchChange={setLiveCatSearch}
              />
            }
            listColumn={
              <>
                <Box flexShrink={0} mb={2}>
                  <SearchBar
                    value={liveChSearch}
                    onChange={setLiveChSearch}
                    placeholder="Channels (2+ chars)"
                  />
                </Box>
                <Box ref={liveListScrollRef} flex={1} minH={0} minW={0} overflowY="auto">
                  <ChannelList
                    key={selectedLiveCategory}
                    channels={filteredLive}
                    viewMode="list"
                    compact
                    scrollElementRef={liveListScrollRef}
                    isFavorite={(id) => isFavorite('live', id)}
                    onToggleFavorite={toggleFavoriteFromLive}
                    onPlay={playLive}
                  />
                </Box>
              </>
            }
          />
        );
      }
      return (
        <VStack align="stretch" spacing={2} flex={1} minH={0}>
          <CategoryRail
            label="Categories"
            categories={displayLiveCategories}
            selectedId={selectedLiveCategory}
            onSelect={setLiveCategory}
            compact
            categorySearch={liveCatSearch}
            onCategorySearchChange={setLiveCatSearch}
          />
          <SearchBar
            compact
            value={liveChSearch}
            onChange={setLiveChSearch}
            placeholder="Channels (2+ chars)"
          />
          <Box ref={liveListScrollRef} flex={1} minH={0} overflowY="auto">
            <ChannelList
              key={selectedLiveCategory}
              channels={filteredLive}
              viewMode="list"
              compact
              scrollElementRef={liveListScrollRef}
              isFavorite={(id) => isFavorite('live', id)}
              onToggleFavorite={toggleFavoriteFromLive}
              onPlay={playLive}
            />
          </Box>
        </VStack>
      );
    }

    if (browseTab === 'movies') {
      if (isLg) {
        return (
          <ResizableBrowseSplit
            categoryColumn={
              <BrowseCategoryColumn
                categories={displayVodCategories}
                selectedId={selectedVodCategory}
                onSelect={setVodCategory}
                categorySearch={vodCatSearch}
                onCategorySearchChange={setVodCatSearch}
              />
            }
            listColumn={
              <>
                <Box flexShrink={0} mb={2}>
                  <SearchBar
                    value={vodItemSearch}
                    onChange={setVodItemSearch}
                    placeholder="Movies (2+ chars)"
                  />
                </Box>
                <Box ref={vodListScrollRef} flex={1} minH={0} overflowY="auto">
                  <PosterGrid items={filteredVod} onSelect={playVod} dense />
                </Box>
              </>
            }
          />
        );
      }
      return (
        <VStack align="stretch" spacing={2} flex={1} minH={0}>
          <CategoryRail
            label="Categories"
            categories={displayVodCategories}
            selectedId={selectedVodCategory}
            onSelect={setVodCategory}
            compact
            categorySearch={vodCatSearch}
            onCategorySearchChange={setVodCatSearch}
          />
          <SearchBar
            compact
            value={vodItemSearch}
            onChange={setVodItemSearch}
            placeholder="Movies (2+ chars)"
          />
          <Box ref={vodListScrollRef} flex={1} minH={0} overflowY="auto">
            <PosterGrid items={filteredVod} onSelect={playVod} dense />
          </Box>
        </VStack>
      );
    }

    const seriesSlice = filteredSeries.slice(0, seriesVisible);
    const seriesRemaining = filteredSeries.length - seriesSlice.length;

    const seriesGrid = (
      <Box>
        <Box
          display="grid"
          gridTemplateColumns={{
            base: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(5, 1fr)',
            xl: 'repeat(6, 1fr)',
          }}
          gap={2}
        >
          {seriesSlice.map((s) => (
            <Box
              key={s.id}
              borderRadius="lg"
              overflow="hidden"
              bg="whiteAlpha.50"
              borderWidth="1px"
              borderColor="whiteAlpha.100"
              cursor="pointer"
              onClick={() => openSeriesDetail(s)}
            >
              <Box aspectRatio="2/3" bg="whiteAlpha.100">
                {s.poster ? (
                  <Image src={s.poster} alt="" w="100%" h="100%" objectFit="cover" />
                ) : (
                  <Flex h="100%" align="center" justify="center" px={1}>
                    <Text fontSize="10px" color="teal.200" textAlign="center" noOfLines={4}>
                      {s.name}
                    </Text>
                  </Flex>
                )}
              </Box>
              <Text fontSize="xs" fontWeight="600" color="white" p={2} noOfLines={2}>
                {s.name}
              </Text>
            </Box>
          ))}
        </Box>
        {seriesRemaining > 0 && (
          <Button
            mt={4}
            size="sm"
            variant="outline"
            borderColor="whiteAlpha.300"
            color="white"
            {...streamOutlineOnDarkHover}
            onClick={() => setSeriesVisible((n) => n + SERIES_PAGE)}
          >
            Load more ({seriesRemaining.toLocaleString()} series)
          </Button>
        )}
      </Box>
    );

    if (isLg) {
      return (
        <ResizableBrowseSplit
          categoryColumn={
            <BrowseCategoryColumn
              categories={displaySeriesCategories}
              selectedId={selectedSeriesCategory}
              onSelect={setSeriesCategory}
              categorySearch={seriesCatSearch}
              onCategorySearchChange={setSeriesCatSearch}
            />
          }
          listColumn={
            <>
              <Box flexShrink={0} mb={2}>
                <SearchBar
                  value={seriesItemSearch}
                  onChange={setSeriesItemSearch}
                  placeholder="Series (2+ chars)"
                />
              </Box>
              <Box flex={1} minH={0} overflowY="auto">
                {seriesGrid}
              </Box>
            </>
          }
        />
      );
    }

    return (
      <VStack align="stretch" spacing={2} flex={1} minH={0}>
        <CategoryRail
          label="Categories"
          categories={displaySeriesCategories}
          selectedId={selectedSeriesCategory}
          onSelect={setSeriesCategory}
          compact
          categorySearch={seriesCatSearch}
          onCategorySearchChange={setSeriesCatSearch}
        />
        <SearchBar
          compact
          value={seriesItemSearch}
          onChange={setSeriesItemSearch}
          placeholder="Series (2+ chars)"
        />
        <Box flex={1} minH={0} overflowY="auto">
          {seriesGrid}
        </Box>
      </VStack>
    );
  };

  const renderMainColumn = () => {
    switch (nav) {
      case 'recents':
        return (
          <VStack align="stretch" spacing={2} overflowY="auto">
            {lastPlayed && (
              <Box p={3} borderRadius="md" bg="teal.900" borderWidth="1px" borderColor="teal.600">
                <Text fontSize="10px" fontWeight="700" color="teal.200" textTransform="uppercase" mb={1}>
                  Last played
                </Text>
                <Text fontWeight="600" color="white" fontSize="sm">
                  {lastPlayed.title}
                </Text>
                <Button mt={2} size="xs" colorScheme="teal" onClick={() => resumeFromRecent(lastPlayed)}>
                  Resume
                </Button>
              </Box>
            )}
            {continueWatching.map((c) => (
              <Flex
                key={c.key}
                p={2}
                borderRadius="md"
                bg="whiteAlpha.50"
                borderWidth="1px"
                justify="space-between"
                align="center"
              >
                <Text fontSize="sm" color="white" noOfLines={1}>
                  {c.title}
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  borderColor="teal.400"
                  color="teal.200"
                  {...streamTealOutlineHover}
                  onClick={() =>
                    playItem({
                      id: c.refId,
                      kind: 'vod',
                      title: c.title,
                      streamUrl: c.streamUrl,
                      poster: c.poster,
                      isLive: false,
                      durationSeconds: c.durationSeconds,
                    })
                  }
                >
                  Resume
                </Button>
              </Flex>
            ))}
            {recents.length === 0 && !lastPlayed ? (
              <EmptyState title="No recents" />
            ) : (
              recents.map((r) => (
                <Flex
                  key={r.key + r.playedAt}
                  p={2}
                  borderRadius="md"
                  bg="whiteAlpha.50"
                  justify="space-between"
                  align="center"
                >
                  <Text fontSize="sm" color="white" noOfLines={1}>
                    {r.title}
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="teal"
                    onClick={() =>
                      playItem({
                        id: r.refId,
                        kind: r.kind,
                        title: r.title,
                        streamUrl: r.streamUrl,
                        poster: r.poster,
                        isLive: r.kind === 'live',
                      })
                    }
                  >
                    Play
                  </Button>
                </Flex>
              ))
            )}
          </VStack>
        );
      case 'servers':
        return (
          <VStack align="stretch" spacing={3} overflowY="auto">
            {servers.length === 0 ? (
              <EmptyState
                title="No servers yet"
                description="Add your first IPTV server — credentials stay in your browser only."
              >
                <Button colorScheme="teal" size="sm" onClick={() => setAddOpen(true)}>
                  Add your first server
                </Button>
              </EmptyState>
            ) : (
              servers.map((s) => {
                const active = activeSourceId === s.id;
                const status = s.lastError ? 'error' : active ? 'active' : 'saved';
                return (
                  <ServerCard
                    key={s.id}
                    server={s}
                    status={status}
                    loading={loading && activeSourceId === s.id}
                    onConnect={() => loadServerCatalog(s)}
                    onEdit={() => setEditTarget(s)}
                    onDuplicate={() => duplicateServer(s.id)}
                    onDelete={() => removeServer(s.id)}
                    onTest={
                      (s.sourceKind ?? 'xtream') === 'xtream' ? () => handleTestXtream(s) : undefined
                    }
                  />
                );
              })
            )}
          </VStack>
        );
      case 'settings':
        return (
          <VStack align="stretch" spacing={4} maxW="md" overflowY="auto">
            <HStack justify="space-between">
              <Text color="whiteAlpha.800" fontSize="sm">
                Live view
              </Text>
              <Button
                size="xs"
                onClick={() =>
                  updateSettings({ viewMode: settings.viewMode === 'grid' ? 'list' : 'grid' })
                }
              >
                {settings.viewMode === 'grid' ? 'Grid' : 'List'}
              </Button>
            </HStack>
            <HStack justify="space-between">
              <Text color="whiteAlpha.800" fontSize="sm">
                Autoplay
              </Text>
              <Button size="xs" onClick={() => updateSettings({ autoplay: !settings.autoplay })}>
                {settings.autoplay ? 'On' : 'Off'}
              </Button>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="xs"
                onClick={() => {
                  const blob = exportLocalConfig();
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(new Blob([blob], { type: 'application/json' }));
                  a.download = 'vorakit-stream-player-config.json';
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                Export JSON
              </Button>
              <Button
                size="xs"
                variant="outline"
                borderColor="whiteAlpha.300"
                color="white"
                {...streamOutlineOnDarkHover}
                onClick={() => {
                  const raw = window.prompt('Paste exported JSON');
                  if (!raw) return;
                  const r = importLocalConfig(raw);
                  if (r.ok) {
                    toast({ title: 'Imported', status: 'success' });
                    window.location.reload();
                  } else toast({ title: 'Import failed', description: r.error, status: 'error' });
                }}
              >
                Import JSON
              </Button>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                {...streamRedOutlineOnDarkHover}
                onClick={() => {
                  if (window.confirm('Clear all Stream Player data on this device?')) {
                    clearAllStreamPlayerData();
                    window.location.reload();
                  }
                }}
              >
                Clear all data
              </Button>
            </HStack>
            <Text fontSize="xs" color="whiteAlpha.500" lineHeight="tall">
              You are responsible for authorized sources only. Playback runs in your browser; VoraKit does not host
              content.
            </Text>
          </VStack>
        );
      default:
        return (
          <Flex direction="column" flex={1} minH={0}>
            {renderBrowseCenter()}
          </Flex>
        );
    }
  };

  const renderRightPanelContent = () => {
    if (playback && !miniPlayer) {
      return (
        <PlayerShell
          item={playback}
          mini={false}
          embedded
          videoMaxH={embeddedPanelVideoMaxH}
          onToggleMini={onExpandMiniPlayer}
          onClose={stopPlayback}
          autoplay={settings.autoplay}
          onProgress={onPlayerProgress}
        />
      );
    }
    if (detailVod) {
      return (
        <VStack align="stretch" spacing={3}>
          <Text fontWeight="700" color="white" fontSize="sm">
            {detailVod.name}
          </Text>
          <Text fontSize="xs" color="whiteAlpha.500">
            {[detailVod.year, detailVod.genre].filter(Boolean).join(' · ')}
          </Text>
          <Button
            leftIcon={isFavorite('vod', detailVod.id) ? <MdFavorite /> : <MdFavoriteBorder />}
            size="xs"
            variant="outline"
            borderColor="whiteAlpha.300"
            color="white"
            {...streamOutlineOnDarkHover}
            onClick={() => toggleFavoriteFromVod(detailVod)}
          >
            Favorite
          </Button>
          <Button colorScheme="teal" size="sm" leftIcon={<MdPlayArrow />} onClick={() => playVod(detailVod)}>
            Play
          </Button>
          <Button size="xs" variant="ghost" color="whiteAlpha.600" {...streamGhostOnDarkHover} onClick={closeVodDetail}>
            Close
          </Button>
        </VStack>
      );
    }
    if (detailSeries) {
      return (
        <SeriesBrowser
          series={detailSeries}
          detail={seriesDetail}
          open
          onClose={closeSeriesDetail}
          onEnsureLoaded={ensureSeriesLoaded}
          isFavoriteSeries={isFavorite('series', detailSeries.id)}
          onToggleFavoriteSeries={() => toggleFavoriteFromSeries(detailSeries)}
          isFavoriteEpisode={(id) => isFavorite('episode', id)}
          onToggleFavoriteEpisode={(ep) =>
            ep.streamUrl && toggleFavoriteFromEpisode(detailSeries, ep.id, ep.title, ep.streamUrl)
          }
          onPlayEpisode={(ep) => playEpisode(ep, detailSeries)}
          embedded
        />
      );
    }
    if (browseTab === 'live' && catalog) {
      return (
        <Text fontSize="sm" color="whiteAlpha.500">
          Select a channel to play. Some providers block browser playback — use “Open in new tab” if needed.
        </Text>
      );
    }
    return null;
  };

  const mainBrowseColumn = (
    <>
      {loading && (
        <HStack px={3} py={1} spacing={3} flexShrink={0} align="center" flexWrap="wrap">
          <Spinner size="xs" color="teal.300" />
          <Text fontSize="xs" color="whiteAlpha.500">
            {loadLabel ?? 'Loading…'}
          </Text>
          {activeServer?.sourceKind === 'm3u_url' && (
            <Button size="xs" variant="outline" borderColor="whiteAlpha.400" onClick={cancelCatalogLoad}>
              Cancel
            </Button>
          )}
        </HStack>
      )}
      <Box flex={1} minH={0} overflow="hidden" display="flex" flexDirection="column" px={{ base: 3, md: 4 }} py={3}>
        {renderMainColumn()}
      </Box>
    </>
  );

  return (
    <Box
      h={SHELL_HEIGHT}
      maxH={SHELL_HEIGHT}
      overflowX="hidden"
      overflowY="visible"
      display="flex"
      flexDirection="column"
      bg="#0b0f14"
      color="white"
      borderRadius="xl"
      borderWidth="1px"
      borderColor="whiteAlpha.200"
    >
      <Flex
        h="72px"
        flexShrink={0}
        px={{ base: 3, md: 4 }}
        align="center"
        justify="space-between"
        gap={3}
        borderBottomWidth="1px"
        borderColor="whiteAlpha.100"
      >
        <HStack spacing={3} minW={0} flex={1}>
          <HStack spacing={2} minW={0}>
            <Heading size="sm" fontWeight="800" letterSpacing="-0.02em" whiteSpace="nowrap">
              Stream Player
            </Heading>
          </HStack>
        </HStack>
        <HStack spacing={2} flexShrink={0} flexWrap="wrap" justify="flex-end">
          {nav === 'browse' && browseTab === 'favorites' && (
            <Box minW={{ base: '120px', md: '200px' }} maxW="280px" display={{ base: 'none', sm: 'block' }}>
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Favorites (2+ chars)"
              />
            </Box>
          )}
          <ServerSwitcher
            activeLabel={switcherLabel}
            servers={servers}
            activeServerId={activeServerIdSetting}
            onSelectServer={(id) => {
              const s = servers.find((x) => x.id === id);
              if (s) void loadServerCatalog(s);
            }}
            onAddServer={() => setAddOpen(true)}
            onManageServers={() => openNav('servers')}
            loading={loading}
          />
          <IconButton
            aria-label="Settings"
            icon={<MdSettings />}
            size="sm"
            variant="outline"
            borderColor="whiteAlpha.300"
            color="white"
            {...streamOutlineOnDarkHover}
            display={{ base: 'flex', md: 'none' }}
            onClick={() => openNav('settings')}
          />
          <Button
            size="sm"
            variant="outline"
            borderColor="whiteAlpha.300"
            color="white"
            {...streamOutlineOnDarkHover}
            display={{ base: 'none', md: 'flex' }}
            onClick={() => openNav('settings')}
          >
            Settings
          </Button>
        </HStack>
      </Flex>

      {nav === 'browse' && browseTab === 'favorites' && (
        <Box display={{ base: 'block', sm: 'none' }} px={3} py={2} borderBottomWidth="1px" borderColor="whiteAlpha.100">
          <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Favorites (2+ chars)" />
        </Box>
      )}

      {(nav === 'browse' || nav === 'recents' || nav === 'settings' || nav === 'servers') && (
        <Box
          position="sticky"
          top="64px"
          zIndex={900}
          flexShrink={0}
          bg="#0b0f14"
        >
          <ModeTabs
            value={browseTab}
            onChange={setBrowseTab}
            browseNavActive={nav === 'browse'}
            recentsActive={nav === 'recents'}
            onOpenRecents={() => openNav('recents')}
          />
        </Box>
      )}

      <Flex flex={1} minH={0} minW={0} overflow="hidden" borderBottomRadius="xl">
        {showRightColumn ? (
          <ResizableBrowsePlayerSplit
            browseColumn={
              <Flex flex={1} minW={0} direction="column" minH={0} h="100%">
                {mainBrowseColumn}
              </Flex>
            }
            playerColumn={
              <RightDetailPanel title={rightPanelTitle} show fillSlot={Boolean(playback && !miniPlayer)}>
                {renderRightPanelContent()}
              </RightDetailPanel>
            }
          />
        ) : (
          <Flex flex={1} minW={0} direction="column" minH={0}>
            {mainBrowseColumn}
          </Flex>
        )}
      </Flex>

      {!isLg && playback && miniPlayer && (
        <PlayerShell
          item={playback}
          mini
          onToggleMini={onCollapseMiniPlayer}
          onClose={stopPlayback}
          autoplay={settings.autoplay}
          onProgress={onPlayerProgress}
        />
      )}

      {!isLg && playback && !miniPlayer && (
        <Drawer isOpen={Boolean(playback)} placement="bottom" onClose={stopPlayback} size="full">
          <DrawerOverlay />
          <DrawerContent bg="#0b0f14">
            <DrawerCloseButton color="white" />
            <DrawerHeader color="white" fontSize="md">
              Player
            </DrawerHeader>
            <DrawerBody>
              <PlayerShell
                item={playback}
                mini={false}
                onToggleMini={onMiniToggleNoop}
                onClose={stopPlayback}
                autoplay={settings.autoplay}
                onProgress={onPlayerProgress}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      {!isLg && !playback && (detailVod || detailSeries) && (
        <Drawer isOpen onClose={detailVod ? closeVodDetail : closeSeriesDetail} placement="bottom" size="lg">
          <DrawerOverlay />
          <DrawerContent bg="#121a22" maxH="85vh">
            <DrawerCloseButton color="white" />
            <DrawerBody pt={10}>
              {detailVod && (
                <VStack align="stretch" spacing={3}>
                  <Text fontWeight="700">{detailVod.name}</Text>
                  <Button colorScheme="teal" onClick={() => playVod(detailVod)}>
                    Play
                  </Button>
                </VStack>
              )}
              {detailSeries && (
                <SeriesBrowser
                  series={detailSeries}
                  detail={seriesDetail}
                  open
                  onClose={closeSeriesDetail}
                  onEnsureLoaded={ensureSeriesLoaded}
                  isFavoriteSeries={isFavorite('series', detailSeries.id)}
                  onToggleFavoriteSeries={() => toggleFavoriteFromSeries(detailSeries)}
                  isFavoriteEpisode={(id) => isFavorite('episode', id)}
                  onToggleFavoriteEpisode={(ep) =>
                    ep.streamUrl && toggleFavoriteFromEpisode(detailSeries, ep.id, ep.title, ep.streamUrl)
                  }
                  onPlayEpisode={(ep) => playEpisode(ep, detailSeries)}
                />
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      {isLg && playback && miniPlayer && (
        <PlayerShell
          item={playback}
          mini
          onToggleMini={onCollapseMiniPlayer}
          onClose={stopPlayback}
          autoplay={settings.autoplay}
          onProgress={onPlayerProgress}
        />
      )}

      <Modal isOpen={Boolean(error)} onClose={clearError} isCentered size="lg" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="#121a22" borderWidth="1px" borderColor="whiteAlpha.200">
          <ModalHeader color="white" fontSize="md" pr={10}>
            {error ? catalogLoadErrorTitle(error) : ''}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Text fontSize="sm" color="whiteAlpha.800" whiteSpace="pre-wrap">
              {error}
            </Text>
          </ModalBody>
          <ModalFooter gap={2} flexWrap="wrap">
            <Button variant="ghost" color="whiteAlpha.700" {...streamGhostOnDarkHover} onClick={clearError}>
              Dismiss
            </Button>
            {activeServer && (
              <Button
                colorScheme="teal"
                variant="outline"
                borderColor="whiteAlpha.300"
                color="white"
                {...streamOutlineOnDarkHover}
                onClick={() => {
                  clearError();
                  void loadServerCatalog(activeServer);
                }}
              >
                Retry connection
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AddServerModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={async (server, { connect }) => {
          await saveStreamServer(server, { connect });
        }}
      />

      <EditServerModal
        server={editTarget}
        isOpen={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        onSave={(s) => {
          updateStreamServer(s);
          if (activeServer?.id === s.id) void loadServerCatalog(s);
        }}
      />
    </Box>
  );
}

export default function StreamPlayer() {
  return (
    <StreamPlayerProvider>
      <Inner />
    </StreamPlayerProvider>
  );
}
